import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from 'react'

/**
 * Get dynamic API URL based on hostname
 */
function getApiUrl(): string {
  const hostname = window.location.hostname

  // Production pattern: account.{slug}.u.software -> api.{slug}.u.software
  const prodMatch = hostname.match(/^account\.(.+)\.u\.software$/)
  if (prodMatch) {
    const orgSlug = prodMatch[1]
    return `https://api.${orgSlug}.u.software`
  }

  // Alternative pattern: {slug}.account.u.software -> {slug}.api.u.software
  const altMatch = hostname.match(/^(.+)\.account\.u\.software$/)
  if (altMatch) {
    const orgSlug = altMatch[1]
    return `https://${orgSlug}.api.u.software`
  }

  // Main u.software: account.u.software -> api.u.software
  if (hostname === 'account.u.software') {
    return 'https://api.u.software'
  }

  // Fly.io deployment
  if (hostname === 'usoftware-account.fly.dev') {
    return 'https://usoftware-api.fly.dev'
  }

  // Development fallback - only for actual localhost
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return import.meta.env.VITE_API_URL || 'http://localhost:8080'
  }

  // Unknown domain - use fly.dev defaults
  console.warn(`Unknown hostname for API: ${hostname}, using fly.dev`)
  return 'https://usoftware-api.fly.dev'
}

const API_URL = getApiUrl()

/**
 * Org branding configuration
 */
export interface OrgBranding {
  logoUrl: string | null
  faviconUrl: string | null
  primaryColor: string | null
  secondaryColor: string | null
  customCss: string | null
  showPoweredBy: boolean
}

/**
 * Full org context for multi-tenant operation
 */
export interface OrgContextValue {
  slug: string | null
  name: string | null
  branding: OrgBranding
  loading: boolean
  error: string | null
}

const defaultBranding: OrgBranding = {
  logoUrl: null,
  faviconUrl: null,
  primaryColor: null,
  secondaryColor: null,
  customCss: null,
  showPoweredBy: true,
}

const defaultContext: OrgContextValue = {
  slug: null,
  name: null,
  branding: defaultBranding,
  loading: true,
  error: null,
}

const OrgContext = createContext<OrgContextValue>(defaultContext)

/**
 * Extract org slug from domain or query params
 * Production: account.{slug}.u.software
 * Development: ?org={slug} or default to null (main u.software)
 */
function extractSlugFromDomain(): string | null {
  const hostname = window.location.hostname

  // Production pattern: account.{slug}.u.software
  const prodMatch = hostname.match(/^account\.(.+)\.u\.software$/)
  if (prodMatch) {
    return prodMatch[1]
  }

  // Also support {slug}.account.u.software pattern
  const altMatch = hostname.match(/^(.+)\.account\.u\.software$/)
  if (altMatch) {
    return altMatch[1]
  }

  // Development: use query param
  const params = new URLSearchParams(window.location.search)
  const orgParam = params.get('org')
  if (orgParam) {
    return orgParam
  }

  // No org context - this is the main u.software account dashboard
  return null
}

interface OrgPublicConfig {
  slug: string
  name: string
  branding?: {
    logo_url?: string | null
    favicon_url?: string | null
    primary_color?: string | null
    secondary_color?: string | null
    custom_css?: string | null
    show_powered_by?: boolean
  }
}

async function fetchOrgConfig(): Promise<OrgPublicConfig> {
  // Org is determined from subdomain by the backend
  const response = await fetch(`${API_URL}/api/org/public-config`)
  if (!response.ok) {
    throw new Error('Failed to load organization configuration')
  }
  return response.json()
}

export interface OrgContextProviderProps {
  children: ReactNode
}

export function OrgContextProvider({ children }: OrgContextProviderProps) {
  const [state, setState] = useState<OrgContextValue>(defaultContext)

  useEffect(() => {
    const slug = extractSlugFromDomain()

    // If no slug, use defaults (main u.software account dashboard)
    if (!slug) {
      setState({
        ...defaultContext,
        loading: false,
      })
      return
    }

    // Fetch org configuration (org determined from subdomain by backend)
    const loadOrg = async () => {
      try {
        const orgConfig = await fetchOrgConfig()

        setState({
          slug,
          name: orgConfig.name,
          branding: {
            logoUrl: orgConfig.branding?.logo_url ?? null,
            faviconUrl: orgConfig.branding?.favicon_url ?? null,
            primaryColor: orgConfig.branding?.primary_color ?? null,
            secondaryColor: orgConfig.branding?.secondary_color ?? null,
            customCss: orgConfig.branding?.custom_css ?? null,
            showPoweredBy: orgConfig.branding?.show_powered_by ?? true,
          },
          loading: false,
          error: null,
        })

        // Apply branding
        applyOrgBranding(orgConfig)
      } catch (err) {
        console.error('Failed to load org config:', err)
        setState({
          ...defaultContext,
          slug,
          loading: false,
          error: 'Failed to load organization configuration',
        })
      }
    }

    loadOrg()
  }, [])

  return <OrgContext.Provider value={state}>{children}</OrgContext.Provider>
}

/**
 * Apply org branding to the page
 */
function applyOrgBranding(orgConfig: OrgPublicConfig) {
  // Apply favicon
  if (orgConfig.branding?.favicon_url) {
    const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement
    if (link) {
      link.href = orgConfig.branding.favicon_url
    } else {
      const newLink = document.createElement('link')
      newLink.rel = 'icon'
      newLink.href = orgConfig.branding.favicon_url
      document.head.appendChild(newLink)
    }
  }

  // Apply CSS variables for colors
  const root = document.documentElement

  if (orgConfig.branding?.primary_color) {
    root.style.setProperty(
      '--org-primary-color',
      orgConfig.branding.primary_color
    )
    // Also generate shades for Tailwind-like usage
    root.style.setProperty(
      '--org-primary-50',
      adjustBrightness(orgConfig.branding.primary_color, 0.95)
    )
    root.style.setProperty(
      '--org-primary-100',
      adjustBrightness(orgConfig.branding.primary_color, 0.9)
    )
    root.style.setProperty(
      '--org-primary-200',
      adjustBrightness(orgConfig.branding.primary_color, 0.8)
    )
    root.style.setProperty(
      '--org-primary-300',
      adjustBrightness(orgConfig.branding.primary_color, 0.6)
    )
    root.style.setProperty(
      '--org-primary-400',
      adjustBrightness(orgConfig.branding.primary_color, 0.4)
    )
    root.style.setProperty(
      '--org-primary-500',
      orgConfig.branding.primary_color
    )
    root.style.setProperty(
      '--org-primary-600',
      adjustBrightness(orgConfig.branding.primary_color, -0.1)
    )
    root.style.setProperty(
      '--org-primary-700',
      adjustBrightness(orgConfig.branding.primary_color, -0.2)
    )
    root.style.setProperty(
      '--org-primary-800',
      adjustBrightness(orgConfig.branding.primary_color, -0.3)
    )
    root.style.setProperty(
      '--org-primary-900',
      adjustBrightness(orgConfig.branding.primary_color, -0.4)
    )
  }

  if (orgConfig.branding?.secondary_color) {
    root.style.setProperty(
      '--org-secondary-color',
      orgConfig.branding.secondary_color
    )
  }

  // Apply custom CSS
  if (orgConfig.branding?.custom_css) {
    const styleId = 'org-custom-css'
    let styleEl = document.getElementById(styleId) as HTMLStyleElement
    if (!styleEl) {
      styleEl = document.createElement('style')
      styleEl.id = styleId
      document.head.appendChild(styleEl)
    }
    styleEl.textContent = orgConfig.branding.custom_css
  }

  // Update page title
  document.title = `${orgConfig.name} - Account`
}

/**
 * Adjust color brightness
 * @param hex - Hex color string (e.g., '#4f46e5')
 * @param percent - Brightness adjustment (-1 to 1, negative = darker)
 */
function adjustBrightness(hex: string, percent: number): string {
  // Remove # if present
  hex = hex.replace(/^#/, '')

  // Parse RGB
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)

  // Adjust brightness
  const adjust = (value: number) => {
    if (percent > 0) {
      // Lighten: blend with white
      return Math.round(value + (255 - value) * percent)
    } else {
      // Darken: blend with black
      return Math.round(value * (1 + percent))
    }
  }

  const newR = Math.min(255, Math.max(0, adjust(r)))
  const newG = Math.min(255, Math.max(0, adjust(g)))
  const newB = Math.min(255, Math.max(0, adjust(b)))

  // Convert back to hex
  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`
}

/**
 * Hook to access org context
 */
export function useOrg(): OrgContextValue {
  return useContext(OrgContext)
}
